import React, { useState, useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate, Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { api, fileUrl } from '../lib/api';
import { useSectionOverride, useRichSection } from '../lib/pageContent';
import { useEditMode, useEditDraft, HeaderEditControl, RichEditControl } from './EditMode';
import { linkifyPita } from '../lib/pita';
import { toast } from 'sonner';
import { Loader2, CheckCircle2 } from 'lucide-react';

export const ISSUE_CATEGORIES = {
  roads: 'Roads / potholes / snow / signs',
  garbage_recycling: 'Garbage / recycling / illegal dumping',
  taxes_payments: 'Taxes / utility bills / payments',
  water_sewer: 'Water / sewer / drainage',
  bylaw: 'By-law / property standards / noise / animals',
  parks_recreation: 'Parks / trails / trees / recreation',
  building_permits: 'Building / septic / permits / zoning',
  customer_service: 'Customer service / no callback / unresolved issue',
  drainage_flooding: 'Drainage / flooding',
  rural_road_safety: 'Rural road safety',
  other: 'Other',
};

// Older reports may still use these keys
export const LEGACY_CATEGORY_LABELS = {
  drainage: 'Ditches & Drainage',
  snow_clearing: 'Snow Clearing',
  parks_facilities: 'Parks & Facilities',
  taxes_budget: 'Taxes & Budget',
  property: 'Property Rights',
  safety: 'Public Safety',
};

export const categoryLabel = (v) => ISSUE_CATEGORIES[v] || LEGACY_CATEGORY_LABELS[v] || (v || '').replace(/_/g, ' ');

// Live categories (admin-editable) with static fallback
export const useIssueCategories = () => {
  const [cats, setCats] = useState(ISSUE_CATEGORIES);
  useEffect(() => {
    api.get('/issue-categories').then((r) => {
      const map = {};
      (r.data?.categories || []).forEach((c) => { if (c && c.value) map[c.value] = c.label || c.value; });
      if (Object.keys(map).length) setCats(map);
    }).catch(() => {});
  }, []);
  return cats;
};

export const STATUS_LABELS = {
  new: 'New',
  needs_review: 'Needs Review',
  sent_to_resident: 'Sent to Resident with CKL Contact Instructions',
  submitted_to_ckl: 'Submitted to CKL',
  waiting_ckl: 'Waiting for CKL Response',
  follow_up: 'Follow-up Required',
  escalated: 'Escalated',
  resolved: 'Resolved',
  closed: 'Closed / No Action Possible',
  in_review: 'In Progress', // legacy records
};

// Short labels for tight badges (mobile-friendly)
export const STATUS_SHORT = {
  new: 'New',
  needs_review: 'Needs Review',
  sent_to_resident: 'Sent to Resident',
  submitted_to_ckl: 'Submitted to CKL',
  waiting_ckl: 'Waiting for CKL',
  follow_up: 'Follow-up Required',
  escalated: 'Escalated',
  resolved: 'Resolved',
  closed: 'Closed',
  in_review: 'In Progress',
};

export const STATUS_STYLES = {
  new: 'bg-slate-100 text-slate-800 border-slate-300',
  needs_review: 'bg-[#FFF7E6] text-[#7A4E00] border-[#F2D08A]',
  sent_to_resident: 'bg-sky-50 text-sky-900 border-sky-200',
  submitted_to_ckl: 'bg-indigo-50 text-indigo-900 border-indigo-200',
  waiting_ckl: 'bg-violet-50 text-violet-900 border-violet-200',
  in_review: 'bg-amber-50 text-amber-900 border-amber-300',
  follow_up: 'bg-[#FFF7E6] text-[#7A4E00] border-[#F2D08A]',
  escalated: 'bg-red-50 text-red-900 border-red-300',
  resolved: 'bg-emerald-50 text-emerald-900 border-emerald-300',
  closed: 'bg-slate-200 text-slate-600 border-slate-300',
};

export const NEWSLETTER_TOPICS = [
  { value: 'general', label: 'Campaign updates' },
  { value: 'taxes_budget', label: 'Taxes & budget' },
  { value: 'roads_infrastructure', label: 'Roads & infrastructure' },
  { value: 'council_meetings', label: 'Council meetings' },
  { value: 'alerts', label: 'Public alerts' },
  { value: 'events', label: 'Events' },
  { value: 'newsroom', label: 'Newsroom stories' },
];

export const NEWS_CATEGORIES = {
  campaign: 'Campaign',
  city_hall: 'City Hall',
  accountability: 'Accountability',
  taxes: 'Taxes',
  taxes_waste: 'Taxes & Waste Watch',
  savings_watch: 'Savings Watch',
  ward8: 'Ward 8',
  community: 'Community',
  imported: 'Imported',
};

export const StatusBadge = ({ status }) => (
  <Badge variant="outline" className={`${STATUS_STYLES[status] || STATUS_STYLES.new} font-semibold text-xs`} data-testid={`status-badge-${status}`}>
    {STATUS_SHORT[status] || STATUS_LABELS[status] || status}
  </Badge>
);

export const SectionHeader = ({ sectionKey, kicker, title, dek, className = '' }) => {
  // Admin -> Page Content overrides (only when customized)
  const o = useSectionOverride(sectionKey);
  const { isAdmin, editMode } = useEditMode();
  const k = o ? o.kicker : kicker;
  const t = (o && o.title) || title;
  const d = o ? (o.intro || dek) : dek;
  const editable = isAdmin && editMode && !!sectionKey && !!o;
  return (
    <div className={`mb-6 sm:mb-8 ${className} ${editable ? 'relative rounded-lg outline-dashed outline-2 outline-[#1a2744]/30 outline-offset-8' : ''}`}>
      {editable && (
        <div className="absolute -top-3 right-0 z-10">
          <HeaderEditControl fullKey={sectionKey} effective={o} label="Edit This Section" testId={`edit-header-${sectionKey}`} />
        </div>
      )}
      {k && <p className="text-xs font-bold uppercase tracking-widest text-[#24406f] mb-2">{k}</p>}
      <h2 className="text-2xl sm:text-3xl font-black text-[#1a2744] tracking-tight leading-tight">{t}</h2>
      {d && <p className="mt-3 text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl">{d}</p>}
    </div>
  );
};

// Page hero / header (Phase 93 light treatment).
// LIGHT overall appearance with DARK readable text on every information page.
// Hero-enabled pages (managed in Admin -> Page Content) also get a full-width
// background image with a strong white gradient from the LEFT so dark text
// never gets lost in the photo. Overlay strength is admin-adjustable and is
// automatically increased on mobile.
const HERO_BTN_STYLES = {
  navy: 'bg-[#1a2744] hover:bg-[#24406f] text-white',
  red: 'bg-[#b91c1c] hover:bg-[#991b1b] text-white',
  black: 'bg-[#0b0f17] hover:bg-black text-white',
};
const HERO_FOCAL_POS = {
  left: '25% center', center: '55% center', right: '78% center',
  'left-top': '25% 10%', 'center-top': '55% 10%', 'right-top': '78% 10%',
};

const resolveHeroImg = (url) => {
  if (!url) return '';
  return (url.startsWith('http') || url.startsWith('/branding/')) ? url : fileUrl(url);
};

const HeroButton = ({ label, url, style, secondary, testId }) => {
  if (!label || !url) return null;
  const cls = secondary
    ? 'inline-flex items-center justify-center h-12 px-5 rounded-md border-2 border-[#1a2744]/70 bg-white/70 text-[#1a2744] font-bold btn-press hover:bg-[#1a2744] hover:text-white transition-colors duration-150'
    : `inline-flex items-center justify-center h-12 px-5 rounded-md font-bold btn-press ${HERO_BTN_STYLES[style] || HERO_BTN_STYLES.navy}`;
  if (url.startsWith('#')) return <a href={url} className={cls} data-testid={testId}>{label}</a>;
  if (url.startsWith('http')) return <a href={url} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testId}>{label}</a>;
  return <Link to={url} className={cls} data-testid={testId}>{label}</Link>;
};

export const PageHeader = ({ pageKey, kicker, title, subtitle, image, logo, logoAlt }) => {
  // Admin -> Page Content: effective config (approved defaults + admin edits)
  const fullKey = pageKey ? `${pageKey}.header` : '';
  const stored = useSectionOverride(fullKey);
  const { isAdmin, editMode } = useEditMode();
  const draft = useEditDraft(fullKey);
  // On-page editing (Phase 96): while an admin previews hero changes, the
  // unsaved draft is merged over the stored config for THIS admin only.
  const o = draft && stored ? { ...stored, ...draft, hero: stored.hero } : stored;
  const editable = isAdmin && editMode && !!fullKey && !!stored;
  const k = o ? o.kicker : kicker;
  const t = (o && o.title) || title;
  const s = o ? (o.intro || subtitle) : subtitle;
  const isHero = !!(o && o.hero);
  if (isHero && o.show === false) {
    if (!editable) return null;
    // Edit mode: the hero is hidden publicly; give the admin a way back in.
    return (
      <div className="bg-slate-100 border-b border-dashed border-slate-300 py-3" data-testid={`page-hero-hidden-${pageKey}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-3">
          <p className="text-xs font-bold text-slate-500">This hero is hidden from visitors. Edit it to turn it back on.</p>
          <HeaderEditControl fullKey={fullKey} effective={stored} label="Edit Hero" />
        </div>
      </div>
    );
  }

  const heroImg = isHero ? resolveHeroImg(o.image_url) : (image || '');
  const ov = isHero ? Math.min(1, Math.max(0.4, (o.overlay ?? 85) / 100)) : 0.86;
  const focal = isHero ? (HERO_FOCAL_POS[o.focal] || HERO_FOCAL_POS.right) : '70% center';
  const hasButtons = isHero && (o.primary_label || o.secondary_label);

  return (
    <div className={`relative bg-[#f3f7ff] border-b border-slate-200 overflow-hidden ${editable ? 'outline-dashed outline-2 -outline-offset-2 outline-[#1a2744]/40' : ''}`} data-testid={pageKey ? `page-hero-${pageKey}` : 'page-header'}>
      {editable && (
        <div className="absolute top-3 right-3 z-20">
          <HeaderEditControl fullKey={fullKey} effective={stored} label={isHero ? 'Edit Hero' : 'Edit Header'} />
        </div>
      )}
      {heroImg && (
        <>
          <img src={heroImg} alt={isHero ? (o.image_alt || '') : ''} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: focal }} data-testid="page-hero-image" />
          {/* Strong white blend from the LEFT (desktop): text side stays readable */}
          <div aria-hidden="true" className="absolute inset-0 hidden sm:block" style={{ background: `linear-gradient(90deg, rgba(255,255,255,${Math.min(1, ov + 0.12)}) 0%, rgba(255,255,255,${ov}) 38%, rgba(255,255,255,${(ov * 0.45).toFixed(2)}) 68%, rgba(255,255,255,0.06) 100%)` }} data-testid="page-hero-gradient" />
          {/* Mobile: stronger overall white so the photo never fights the text */}
          <div aria-hidden="true" className="absolute inset-0 sm:hidden" style={{ background: `linear-gradient(180deg, rgba(255,255,255,${Math.min(1, ov + 0.08)}) 0%, rgba(255,255,255,${Math.min(1, ov + 0.02)}) 55%, rgba(255,255,255,${(ov * 0.72).toFixed(2)}) 100%)` }} />
        </>
      )}
      <div className={`relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center ${heroImg ? 'min-h-[300px] sm:min-h-[340px] lg:min-h-[380px] py-10' : 'py-12 sm:py-16'}`}>
        <div className={logo ? 'flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8' : ''}>
          {logo && (
            <div className="bg-white rounded-2xl p-3 shrink-0 w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center shadow-lg border border-slate-200" data-testid="page-header-logo-card">
              <img src={logo} alt={logoAlt || 'Public Issue Tracker App (PITA) logo'} className="max-w-full max-h-full object-contain" data-testid="page-header-logo" />
            </div>
          )}
          <div className="min-w-0 max-w-2xl">
            {k && <p className="text-xs font-bold uppercase tracking-widest text-[#24406f] mb-3">{k}</p>}
            <h1 className="hero-h1 text-3xl sm:text-4xl lg:text-5xl text-[#0f172a] whitespace-pre-line" data-testid="page-header-title">{t}</h1>
            {s && <p className="mt-3 text-base sm:text-lg text-slate-700 font-medium leading-relaxed max-w-xl">{s}</p>}
            {hasButtons && (
              <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
                <HeroButton label={o.primary_label} url={o.primary_url} style={o.primary_style} testId={`page-hero-${pageKey}-primary`} />
                <HeroButton label={o.secondary_label} url={o.secondary_url} secondary testId={`page-hero-${pageKey}-secondary`} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Rich text renderer. Normalizes non-breaking spaces (common when content is
// pasted from Word/Facebook into the editor) so text can wrap naturally on
// mobile instead of forcing sideways scrolling or mid-word breaks.
export const Rich = ({ html, className = '' }) => {
  const navigate = useNavigate();
  // Sanitize first, then auto-link plain "PITA" mentions to /issue-tracker
  // (skips existing links/buttons/headings so no nested anchors are created).
  // Sanitized again AFTER the DOM transform so the markup handed to React is
  // always the direct output of DOMPurify (guards against mutation quirks).
  const processed = useMemo(
    () => DOMPurify.sanitize(linkifyPita(DOMPurify.sanitize((html || '').replace(/&nbsp;|\u00a0/g, ' ')))),
    [html],
  );
  const onClick = (e) => {
    const a = e.target.closest && e.target.closest('a.pita-link');
    if (a) { e.preventDefault(); navigate('/issue-tracker'); }
  };
  return <div className={`w8-prose ${className}`} onClick={onClick} dangerouslySetInnerHTML={{ __html: processed }} />;
};

/* WYSIWYG section slot (Phase 93). When Steve saves content for this section
   in Admin -> Page Content, his text replaces the built-in wording below.
   Blank = the approved built-in wording (children) stays.
   Phase 96: while Edit Mode is ON (admins only), the slot is outlined with an
   EDIT THIS SECTION button that opens the same WYSIWYG right on the page. */
export const RichSection = ({ id, className = '', children = null }) => {
  const html = useRichSection(id);
  const { isAdmin, editMode } = useEditMode();
  const editable = isAdmin && editMode && !!id;

  const body = html ? (
    <div className={editable ? '' : className} data-testid={`rich-section-${id}`}>
      <Rich html={html} />
    </div>
  ) : children;

  if (!editable) return body;

  return (
    <div className={`relative rounded-lg outline-dashed outline-2 outline-[#1a2744]/30 outline-offset-4 ${html ? className : ''}`} data-testid={`rich-section-editable-${id}`}>
      <div className="absolute -top-3 right-2 z-10">
        <RichEditControl richKey={id} currentHtml={html} />
      </div>
      {body || (
        <p className="px-4 py-3 text-xs font-bold text-slate-400">Empty section. Click Edit This Section to add content here.</p>
      )}
    </div>
  );
};

export const Loading = ({ label = 'Loading…' }) => (
  <div className="flex items-center justify-center py-16 text-slate-500" data-testid="loading-indicator">
    <Loader2 className="h-6 w-6 animate-spin mr-2" />
    <span>{label}</span>
  </div>
);

export const EmptyState = ({ title, subtitle }) => (
  <div className="text-center py-12 px-4" data-testid="empty-state">
    <p className="text-lg font-bold text-[#1a2744]">{title}</p>
    {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
  </div>
);

export const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso.length === 10 ? iso + 'T12:00:00' : iso);
    return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return iso;
  }
};

export const hexToRgba = (hex, alpha) => {
  const h = (hex || '#1a2744').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const overlayStyle = (overlay) => {
  if (!overlay) return { background: 'linear-gradient(90deg, rgba(26,39,68,0.92) 0%, rgba(26,39,68,0.55) 55%, rgba(26,39,68,0.15) 100%)' };
  const op = (overlay.opacity ?? 75) / 100;
  const color = overlay.color || '#1a2744';
  switch (overlay.type) {
    case 'blend_left':
      return { background: `linear-gradient(90deg, ${hexToRgba(color, op)} 0%, ${hexToRgba(color, op * 0.6)} 55%, ${hexToRgba(color, op * 0.15)} 100%)` };
    case 'blend_right':
      return { background: `linear-gradient(270deg, ${hexToRgba(color, op)} 0%, ${hexToRgba(color, op * 0.6)} 55%, ${hexToRgba(color, op * 0.15)} 100%)` };
    case 'tone':
      return { background: hexToRgba(color, op * 0.6), mixBlendMode: 'multiply' };
    case 'solid':
    default:
      return { background: hexToRgba(color, op) };
  }
};

// Newsletter subscribe form — reused across the site
let _emailActiveCache = null;
const fetchEmailActive = async () => {
  if (_emailActiveCache === null) {
    try {
      const r = await api.get('/settings');
      _emailActiveCache = !!r.data.email_delivery_active;
    } catch {
      _emailActiveCache = false;
    }
  }
  return _emailActiveCache;
};

export const SubscribeForm = ({ compact = false, testId = 'subscribe-form' }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [topics, setTopics] = useState(['general', 'alerts']);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [emailActive, setEmailActive] = useState(true);

  React.useEffect(() => { fetchEmailActive().then(setEmailActive); }, []);

  const toggleTopic = (t) => setTopics((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await api.post('/subscribe', { email, name, topics: topics.length ? topics : ['general'] });
      setDone(true);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Subscription failed. Please check your email address.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="space-y-2" data-testid="subscribe-success">
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <CheckCircle2 className="h-6 w-6 text-emerald-700 shrink-0" />
          <p className="text-emerald-900 font-semibold">You're subscribed. Thank you for staying informed!</p>
        </div>
        {!emailActive && (
          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5 leading-relaxed" data-testid="subscribe-pending-note">
            Signup is open and your subscription has been saved. Email delivery will begin once alerts are activated.
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={testId}>
      {!compact && (
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="h-12 bg-white" data-testid="subscribe-name-input" />
      )}
      <div className="flex flex-col sm:flex-row gap-3">
        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" className="h-12 bg-white sm:flex-1 shrink-0" data-testid="subscribe-email-input" />
        <Button type="submit" disabled={busy} className="h-12 px-6 bg-[#b91c1c] hover:bg-[#991b1b] text-white font-bold btn-press" data-testid="subscribe-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
        </Button>
      </div>
      {!compact && (
        <div className="flex flex-wrap gap-x-4 gap-y-2 pt-1">
          {NEWSLETTER_TOPICS.map((t) => (
            <label key={t.value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox checked={topics.includes(t.value)} onCheckedChange={() => toggleTopic(t.value)} data-testid={`subscribe-topic-${t.value}`} />
              {t.label}
            </label>
          ))}
        </div>
      )}
      {!emailActive && (
        <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5 leading-relaxed" data-testid="subscribe-preparing-note">
          Signup is open. Email delivery will begin once alerts are activated.
        </p>
      )}
    </form>
  );
};
